import { airbrake } from "../airbrake";
import axios, { AxiosInstance } from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { coreLibraryInitialised } from "@glamcorner/core-cf-lib/lib/common/gcsession/utils";
import { hostnameToStorefront } from "@glamcorner/core-cf-lib/lib/client/Storefront";
import { TiersAccessible } from "@glamcorner/gc-types/lib/subscription";
import { ProfileWithSubscription } from "@glamcorner/gc-types/generated/types";
import {
  CatalogueFilterQuery,
  CatalogueSizeAvailabilityBody,
  DeliveryCalendarQuery,
  DeliveryCalendarResult,
  ProductCalendarQuery,
  ProductCalendarResult,
  SearchAppResult,
  SearchAppSizesApiResponse,
  StylePageStyle,
  StylingPreferences,
} from "../../common/interfaces";

const SEARCH_APP_PATH = "/se?path=";
const DELIVERY_CALENDAR_PATH = "/delivery-calendar";
const PRODUCT_CALENDAR_PATH = "/product-calendar";
/** Request timeout, in milliseconds. */
const TIMEOUT = 10_000;

export interface FilterRequestBody {
  query: CatalogueFilterQuery;
  styleCodes?: string[];
}

export class ServerGateway {
  private readonly axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: TIMEOUT,
    });
  }

  async filterRequest(body: FilterRequestBody): Promise<SearchAppResult | null> {
    const requestDataString = JSON.stringify({ body });
    const options = {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json",
      },
    };

    try {
      const result = await this.axiosInstance.post(
        `${this.getCataloguePath()}/api/v3/catalogue`,
        requestDataString,
        options
      );
      return result.data.body;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async deliveryCalendar(query: DeliveryCalendarQuery): Promise<DeliveryCalendarResult | null> {
    const postcode = query.postcode;
    const requestPath = `${DELIVERY_CALENDAR_PATH}${postcode ? `?postcode=${postcode}` : ""}`;
    const url = `${SEARCH_APP_PATH}${encodeURIComponent(requestPath)}`;

    try {
      const {
        data: { body },
      } = await this.axiosInstance.get(url);
      return body;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async productCalendar(query: ProductCalendarQuery): Promise<ProductCalendarResult | null> {
    const storefront = hostnameToStorefront();

    const { productCode, rentalDays, postcode } = query;

    const requestPath = `${PRODUCT_CALENDAR_PATH}/${productCode}?days=${rentalDays}&postcode=${postcode}&storefront=${storefront}`;

    const url = `${SEARCH_APP_PATH}${encodeURIComponent(requestPath)}`;

    try {
      const {
        data: { body },
      } = await this.axiosInstance.get(url);
      return body;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async subscriptionAvailabilities(
    styleCode: string,
    tiers: TiersAccessible,
    startDate?: string,
    endDate?: string
  ): Promise<CatalogueSizeAvailabilityBody | null> {
    let dateRangeQuery = "";
    if (startDate && endDate) {
      dateRangeQuery = `&start_date=${startDate}&end_date=${endDate}`;
    }

    const tiersParam = `&tiers=${tiers.join(",")}`;

    const url = `/api/v1/subscription-availabilities?style_codes=${styleCode}${tiersParam}${dateRangeQuery}`;

    try {
      const {
        data: { body },
      } = await this.axiosInstance.get(url);

      return body;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async getCustomerProfile(): Promise<ProfileWithSubscription | undefined> {
    try {
      if (coreLibraryInitialised()) {
        return await GlamCornerCore.profileService.getProfile();
      }
      return;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return;
    }
  }

  async getBuiltBoxThisBillingPeriod(): Promise<boolean> {
    try {
      if (coreLibraryInitialised()) {
        return await GlamCornerCore.profileService.getBuiltBoxThisBillingPeriod();
      }
      return false;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return false;
    }
  }

  async querySizes(styleCodes: string[]): Promise<SearchAppSizesApiResponse | null> {
    try {
      const {
        data: { body },
      } = await this.axiosInstance.get(`/api/v1/sizes?style_codes=${styleCodes.join(",")}`);

      return body;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async queryStylePageStyle(styleCode: string): Promise<StylePageStyle | null> {
    try {
      const response = await this.axiosInstance.get(`${this.getCataloguePath()}/api/v1/stylePage/style/${styleCode}`);

      return response.data.body as StylePageStyle;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return null;
    }
  }

  async setStylingPreferences(stylingPreferences: StylingPreferences): Promise<undefined> {
    const token = await this.getJWT();
    if (!token) {
      return;
    }

    try {
      await this.axiosInstance.put(
        "/gc-api/v1/profile/stylingPreferences",
        { ...stylingPreferences },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return;
    } catch (e) {
      console.error(e);
      airbrake.notify(e);
      return;
    }
  }

  private getCataloguePath() {
    let baseURL = "https://catalogue.glamcorner.com.au";
    const host = "" || window?.location?.host;

    if (/staging/.test(host)) {
      baseURL = "https://catalogue.staging.glamcorner.com.au";
    } else if (/website.local/.test(host)) {
      baseURL = `${window.location.protocol}//catalogue.local.glamcorner.com.au`;
    }

    return baseURL;
  }

  private async getJWT(): Promise<string | undefined> {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { loggedIn, token } = await GlamCornerCore.gcSession?.getSessionValues("loggedIn", "token");

    if (loggedIn) {
      if (token) {
        const { exp } = jwtDecode<JwtPayload>(token);

        if (exp && exp * 1000 - TIMEOUT > Date.now()) {
          return token;
        }
      }

      try {
        const response = await this.axiosInstance.get(`/gcsession?_=${Date.now()}`);
        return response?.data?.token;
      } catch (e) {
        console.error(e);
        airbrake.notify(e);
        return undefined;
      }
    } else {
      return undefined;
    }
  }
}
